import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { ATTRIBUTE_LABELS_REFERENCE_DATA_TYPES, AttributeLabelsReferenceData } from "@constants/reference-data";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getAttributeLabelsReduxRequest } from "@redux-requests/attribute-labels";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { AddAttributeLabelModal } from "@containers/modals/add-attribute-label-modal/add-attribute-label-modal";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { AttributeLabelsTableContainer } from "./axn-attribute-labels-table-container/attribute-labels-table-container";
import { getTableSettingsState } from "@redux/selectors/root-selectors";
import { getAttributeLabelsDataState } from "@redux/selectors/attribute-labels-selectors";

export const AttributeLabelsContainer = () => {
    const modalContext = useModalContext()
    const dispatch = useDispatch();

    const { attributeLabels: attributeLabelsTableState } = useSelector(getTableSettingsState);
    const {
        isLoadingAxnAttributeLabels,
        axnAttributeLabels,
    } = useSelector(getAttributeLabelsDataState);
    const referenceData = useReferenceDataSelector<AttributeLabelsReferenceData>(ATTRIBUTE_LABELS_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadAxnAttributeLabels, setShouldLoadAxnAttributeLabels] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.attributeLabels && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ATTRIBUTE_LABELS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.ATTRIBUTE_LABELS
            );
        }
    }, [referenceData.isLoading.attributeLabels, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnAttributeTypes
        );
    }, [
        referenceData.axnAttributeTypes
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingAxnAttributeLabels && shouldLoadAxnAttributeLabels) {
            setShouldLoadAxnAttributeLabels(false);
            getAttributeLabelsReduxRequest(dispatch)(referenceData.axnAttributeTypes);
        }
    }, [isReferenceDataPresent, isLoadingAxnAttributeLabels, shouldLoadAxnAttributeLabels]);

    const renderAddAttributeLabel = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddAttributeLabelModal
                onModalClose={onModalClose}
                axnAttributeTypes={referenceData.axnAttributeTypes}
            />
        );
    }, [referenceData.axnAttributeTypes]);

    const isLoading = useMemo(() => {
        return (
            isLoadingAxnAttributeLabels ||
            referenceData.isLoading.attributeLabels
        );
    }, [
        isLoadingAxnAttributeLabels,
        referenceData.isLoading.attributeLabels
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnAttributeLabels &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadAxnAttributeLabels
        );
    }, [isLoading, axnAttributeLabels, isReferenceDataPresent, shouldLoadReferenceData, shouldLoadAxnAttributeLabels]);


    const headerButton = useMemo(() => {
        const onClick = () => {
            modalContext.setModalState({
                modalType: ModalTypes.AddAttributeLabel,
                renderFunction: renderAddAttributeLabel
            });
        };
        return (
            <ButtonWithIcon
                id="add-attribute-label-button"
                title={resources.pages.attributeLabels.headerButton}
                text={resources.pages.attributeLabels.headerButton}
                onClick={onClick}
                icon={faPlusCircle}
                disabled={isLoading || !isLoaded}
            />
        );
    }, [isLoading, isLoaded, renderAddAttributeLabel]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader title={resources.pages.attributeLabels.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <AttributeLabelsTableContainer
                        axnAttributeLabels={axnAttributeLabels}
                        tableState={attributeLabelsTableState}
                        isPageHeight={true}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};